import { Link } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { ArrowRight, Sun, MoonStar, Compass } from "lucide-react";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { useT } from "@/lib/i18n";

const IMG_HERO =
  "https://cf.bstatic.com/xdata/images/hotel/max1024x768/218305753.jpg?k=d317bb52c41e7d1c285ba13e7cef0e69b980d3b97ee5fa0b908ac115a8f8eeb0&o=";
const IMG_ROOM_VIEW =
  "https://cf.bstatic.com/xdata/images/hotel/max1024x768/264165080.jpg?k=fc0566d4f99ef7608a9d99a2fc3ad736fe89f70e11e093ebe8f3c08412ea2c22&o=";
const IMG_VASCA =
  "https://cf.bstatic.com/xdata/images/hotel/max1024x768/522556202.jpg?k=21582021b08150fa190f408d3cedea27998d5320b37ae40fcea67f3b9e990548&o=";
const IMG_BREAKFAST =
  "https://cf.bstatic.com/xdata/images/hotel/max1024x768/600321143.jpg?k=5f5448caae0036603b716ca44227be97eefdb6d455ab3a312227e2be7816560a&o=";
const IMG_HALLWAY =
  "https://cf.bstatic.com/xdata/images/hotel/max1024x768/264096824.jpg?k=1cb0f56575aa8b979856c45dc50007fca343503621d5d1728b88a0973b1cce9a&o=";
const IMG_LOUNGE =
  "https://cf.bstatic.com/xdata/images/hotel/max1024x768/299335806.jpg?k=920e932f70b841c673edb46dc28d3474b3dc4d695e7a0a77789614b93198b99c&o=";
const IMG_AMALFI =
  "https://images.unsplash.com/photo-1583844056361-4418a8f2a985?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxhbWFsZmklMjBjb2FzdCUyMGl0YWx5fGVufDB8fHx8MTc4NDIzOTI0OXww&ixlib=rb-4.1.0&q=85";
const IMG_POSITANO =
  "https://images.unsplash.com/photo-1561956021-947f09ae0101?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwzfHxhbWFsZmklMjBjb2FzdCUyMGl0YWx5fGVufDB8fHx8MTc4NDIzOTI0OXww&ixlib=rb-4.1.0&q=85";
const IMG_SANTUARIO_POMPEI =
  "https://upload.wikimedia.org/wikipedia/commons/thumb/b/b7/Basilica_Santuario_della_Beata_Vergine_del_Santo_Rosario_di_Pompei_-.jpg/1920px-Basilica_Santuario_della_Beata_Vergine_del_Santo_Rosario_di_Pompei_-.jpg";
const IMG_NAPOLI_PLEBISCITO =
  "https://upload.wikimedia.org/wikipedia/commons/thumb/b/b5/Piazza_del_Plebiscito_-_Naples%2C_Italy_-_panoramio.jpg/1280px-Piazza_del_Plebiscito_-_Naples%2C_Italy_-_panoramio.jpg";

export default function LandingPage() {
  const { t } = useT();
  const distances = [
    {
      key: "costiera",
      name: "Costiera Amalfitana",
      km: "30 km",
      minutes: "45 min",
      img: IMG_POSITANO,
    },
    {
      key: "salerno",
      name: "Salerno",
      km: "15 km",
      minutes: "20 min",
      img: IMG_ROOM_VIEW,
    },
    {
      key: "pompei",
      name: "Pompei",
      km: "10 km",
      minutes: "15 min",
      img: IMG_SANTUARIO_POMPEI,
    },
    {
      key: "napoli",
      name: "Napoli",
      km: "30 km",
      minutes: "35 min",
      img: IMG_NAPOLI_PLEBISCITO,
    },
  ];

  return (
    <div data-testid="landing-page" className="min-h-screen bg-background">
      <SiteHeader transparent />

      {/* HERO */}
      <section className="relative h-[92vh] w-full overflow-hidden">
        <img
          src={IMG_HERO}
          alt="Hotel Due Pini Conte"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-vignette" />
        <div className="relative z-10 h-full flex flex-col justify-end pb-20 md:pb-28">
          <div className="max-w-7xl w-full mx-auto px-6 md:px-10">
            <div className="max-w-3xl">
              <div className="label-eyebrow text-white/90 mb-6">
                {t("landing.eyebrow")}
              </div>
              <h1
                className="font-display font-light text-white text-5xl sm:text-6xl md:text-7xl leading-[0.95] tracking-tight"
                style={{ textShadow: "0 2px 24px rgba(0,0,0,0.35)" }}
              >
                {t("landing.hero.line1")}
                <br />
                {t("landing.hero.line2")}
              </h1>
              <p
                className="mt-6 text-white/90 text-lg md:text-xl max-w-2xl leading-relaxed"
                style={{ textShadow: "0 1px 8px rgba(0,0,0,0.4)" }}
              >
                {t("landing.hero.desc")}
              </p>

              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Link
                  to="/prenota/pernottamenti"
                  data-testid="cta-overnight"
                  className="btn-primary pill px-8 py-4 text-base font-medium inline-flex items-center gap-3"
                >
                  <MoonStar className="w-5 h-5" />
                  {t("landing.cta.overnight")}
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/prenota/dayuse"
                  data-testid="cta-dayuse"
                  className="btn-secondary pill px-8 py-4 text-base font-medium inline-flex items-center gap-3"
                >
                  <Sun className="w-5 h-5" />
                  {t("landing.cta.dayuse")}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32 grid md:grid-cols-12 gap-10 items-start">
        <div className="md:col-span-5">
          <div className="label-eyebrow mb-4">{t("landing.intro.eyebrow")}</div>
          <h2 className="font-display font-light text-4xl md:text-5xl leading-tight text-primary">
            {t("landing.intro.title")}
          </h2>
        </div>
        <div className="md:col-span-7 md:pl-10 md:border-l md:border-border">
          <p className="text-lg leading-relaxed text-foreground/85">
            {t("landing.intro.desc.1")}
            <strong>{t("landing.intro.desc.hotel")}</strong>
            {t("landing.intro.desc.2")}
            <strong>{t("landing.intro.desc.bnb")}</strong>
            {t("landing.intro.desc.3")}
          </p>
          <div className="grid grid-cols-3 gap-6 mt-10">
            <div>
              <div className="font-display text-4xl text-secondary">15</div>
              <div className="text-sm text-muted-foreground mt-1">
                {t("landing.stat.hotel_rooms")}
              </div>
            </div>
            <div>
              <div className="font-display text-4xl text-secondary">1</div>
              <div className="text-sm text-muted-foreground mt-1">
                {t("landing.stat.suite")}
              </div>
            </div>
            <div>
              <div className="font-display text-4xl text-secondary">3</div>
              <div className="text-sm text-muted-foreground mt-1">
                {t("landing.stat.bnb_rooms")}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DINTORNI - Bento */}
      <section id="dintorni" className="bg-muted/60 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between mb-14 gap-6 flex-wrap">
            <div>
              <div className="label-eyebrow mb-3 flex items-center gap-2">
                <Compass className="w-3 h-3" /> {t("landing.dintorni.eyebrow")}
              </div>
              <h2 className="font-display font-light text-4xl md:text-5xl text-primary leading-tight max-w-2xl">
                {t("landing.dintorni.title")}
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {distances.map((d, i) => (
              <div
                key={d.name}
                data-testid={`distance-${d.name.toLowerCase().replace(/\s+/g, "-")}`}
                className={`relative overflow-hidden rounded-2xl group ${
                  i === 0
                    ? "md:col-span-5 md:row-span-2 min-h-[380px]"
                    : i === 2
                      ? "md:col-span-7 min-h-[240px]"
                      : "md:col-span-7 min-h-[240px]"
                }`}
              >
                <img
                  src={d.img}
                  alt={d.name}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="relative h-full flex flex-col justify-end p-8 text-white">
                  <div className="text-xs tracking-[0.28em] uppercase text-white/70 mb-2">
                    {d.km} · {d.minutes}
                  </div>
                  <div className="font-display text-3xl md:text-4xl mb-2">
                    {d.name}
                  </div>
                  <div className="text-white/85 text-sm max-w-md">
                    {t(`dist.${d.key}.desc`)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section
        id="gallery"
        className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32"
      >
        <div className="mb-14 max-w-2xl">
          <div className="label-eyebrow mb-3">{t("landing.gallery.eyebrow")}</div>
          <h2 className="font-display font-light text-4xl md:text-5xl text-primary leading-tight">
            {t("landing.gallery.title")}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          <img
            src={IMG_ROOM_VIEW}
            alt="Camera matrimoniale"
            className="w-full h-96 object-cover rounded-2xl md:col-span-2"
          />
          <img
            src={IMG_VASCA}
            alt="Bagno privato"
            className="w-full h-96 object-cover rounded-2xl"
          />
          <img
            src={IMG_BREAKFAST}
            alt="Colazione buffet"
            className="w-full h-72 object-cover rounded-2xl"
          />
          <img
            src={IMG_LOUNGE}
            alt="Sala interna"
            className="w-full h-72 object-cover rounded-2xl"
          />
          <img
            src={IMG_HALLWAY}
            alt="Corridoio"
            className="w-full h-72 object-cover rounded-2xl"
          />
        </div>
      </section>

      {/* CTA BAND */}
      <section className="bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h3 className="font-display font-light text-4xl md:text-5xl leading-tight">
              {t("landing.band.title")}
            </h3>
            <p className="text-white/75 mt-4 max-w-lg">
              {t("landing.band.desc")}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 md:justify-end">
            <Link
              to="/prenota/pernottamenti"
              data-testid="cta-band-overnight"
              className="bg-white text-primary pill px-8 py-4 font-medium hover:bg-white/90 inline-flex items-center gap-2"
            >
              <MoonStar className="w-4 h-4" /> {t("landing.band.overnight")}
            </Link>
            <Link
              to="/prenota/dayuse"
              data-testid="cta-band-dayuse"
              className="btn-secondary pill px-8 py-4 font-medium inline-flex items-center gap-2"
            >
              <Sun className="w-4 h-4" /> {t("landing.band.dayuse")}
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
      <WhatsAppFloat />
    </div>
  );
}
